import { useEffect, useRef, useState } from "react";
import { isFullAccessProfile } from "../../../utils/helpers/permissions";
import { formatIDR } from "../../../utils/helpers/formatIDR";
import "../../PurchasePage/FormPurchase/style.css";
import "./style.css";

const METHODS = [
  { key: "cash", label: "Tunai / Cash", path: "M3 6h18v12H3zM7 10h.01M17 14h.01", circle: true },
  { key: "qris", label: "QRIS", path: "M3 3h6v6H3zM15 3h6v6h-6zM3 15h6v6H3zM15 15h2v2h-2zM21 15v3M15 21h6" },
  { key: "transfer", label: "Transfer bank", path: "M3 10h18M4 10V8l8-5 8 5v2M5 10v8M10 10v8M14 10v8M19 10v8M3 21h18M4 18h16" },
  { key: "other", label: "Lainnya / belum terklasifikasi", path: "M5 4h14v16H5zM8 8h8M8 12h8M8 16h4" },
];

const formatDate = (value) => {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  return Number.isNaN(date.getTime()) ? "" : new Intl.DateTimeFormat("id-ID", {
    day: "numeric", month: "short", year: "numeric",
  }).format(date);
};

const PaidRevenueModal = ({ summary, dateRange, loading = false, onRetry, userProfile }) => {
  const [showAccounts, setShowAccounts] = useState(false);
  const modalRef = useRef(null);
  const accountsTrigger = useRef(null);
  const backButton = useRef(null);
  const canViewAccounts = isFullAccessProfile(userProfile);
  const accountView = canViewAccounts && showAccounts;
  const filterKey = JSON.stringify([dateRange, summary?.filters]);
  useEffect(() => { setShowAccounts(false); }, [filterKey, canViewAccounts]);
  const returnToSummary = (event) => {
    event?.stopPropagation();
    setShowAccounts(false);
    requestAnimationFrame(() => accountsTrigger.current?.focus());
  };
  useEffect(() => {
    const modal = modalRef.current;
    const reset = () => setShowAccounts(false);
    const beforeHide = (event) => {
      if (!accountView) return;
      event.preventDefault();
      setShowAccounts(false);
      requestAnimationFrame(() => accountsTrigger.current?.focus());
    };
    modal?.addEventListener("hidden.bs.modal", reset);
    modal?.addEventListener("hide.bs.modal", beforeHide);
    return () => {
      modal?.removeEventListener("hidden.bs.modal", reset);
      modal?.removeEventListener("hide.bs.modal", beforeHide);
    };
  }, [accountView]);
  useEffect(() => { if (accountView) backButton.current?.focus(); }, [accountView]);
  const breakdown = summary?.paid_revenue_breakdown;
  const hasBreakdown = Array.isArray(breakdown?.methods);
  const total = Number(summary?.paid_revenue || 0);
  const amounts = Object.fromEntries((breakdown?.methods || []).map((method) => [method.payment_method, Number(method.amount || 0)]));
  const start = formatDate(dateRange?.startDate);
  const end = formatDate(dateRange?.endDate);
  const period = start && end ? (start === end ? start : `${start} – ${end}`)
    : start ? `Sejak ${start}` : end ? `Sampai ${end}` : "Semua tanggal transaksi";

  return (
    <div
      ref={modalRef}
      className="modal modal-blur fade sale-paid-revenue-modal"
      id="modal-sale-paid-revenue"
      tabIndex="-1"
      role="dialog"
      aria-modal="true"
      aria-labelledby="sale-paid-revenue-title"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.3)" }}>
      <div className={`modal-dialog modal-dialog-centered${accountView ? " sale-paid-accounts-dialog" : ""}`} role="document">
        <div className="modal-content purchase-modal-content">
          <div className="modal-header purchase-modal-header">
            <div className="d-flex align-items-center gap-3">
              <span className="purchase-modal-icon bg-primary-lt text-primary" aria-hidden="true">
                <svg viewBox="0 0 24 24"><path d="M3 6h18v12H3zM7 10h.01M17 14h.01" /><circle cx="12" cy="12" r="2" /></svg>
              </span>
              <div>
                <div className="text-secondary small fw-semibold text-uppercase">Ringkasan penjualan</div>
                <h5 className="modal-title mb-0" id="sale-paid-revenue-title">{accountView ? "Omzet Lunas per Akun" : "Rincian Omzet Lunas"}</h5>
              </div>
            </div>
            <button type="button" className="btn-close" data-bs-dismiss={accountView ? undefined : "modal"} onClick={accountView ? returnToSummary : undefined} aria-label={accountView ? "Kembali ke rincian omzet" : "Tutup rincian omzet lunas"} />
          </div>

          <div className="modal-body purchase-modal-body">
            <div className="sale-paid-revenue-period text-secondary small">
              <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M4 5h16v16H4zM16 3v4M8 3v4M4 11h16" /></svg>
              <span>{period}</span>
            </div>
            {loading ? (
              <div className="sale-paid-revenue-state" role="status">
                <span className="spinner-border spinner-border-sm text-primary" aria-hidden="true" />
                <span>Memuat rincian omzet...</span>
              </div>
            ) : !hasBreakdown ? (
              <div className="sale-paid-revenue-state text-center" role="status">
                <p className="mb-0">Rincian omzet belum tersedia untuk filter ini. Silakan muat ulang.</p>
                <button type="button" className="btn btn-primary btn-sm" onClick={onRetry}>Muat ulang</button>
              </div>
            ) : accountView ? (
              <>
                <div className="mb-3"><span className="text-secondary">Total omzet lunas</span><h3 className="mb-0">{formatIDR(total)}</h3></div>
                {!Array.isArray(breakdown.accounts) ? <div className="sale-paid-revenue-state" role="status"><p>Rincian akun belum tersedia. Silakan muat ulang.</p><button className="btn btn-primary" onClick={onRetry}>Muat ulang</button></div> : !breakdown.accounts.length ? <p className="text-center text-secondary py-4">Belum ada transaksi lunas pada periode dan filter ini.</p> : (
                  <div className="table-responsive border rounded-3" tabIndex="0" role="region" aria-label="Omzet lunas per akun kasir dan seller">
                    <table className="table table-vcenter card-table sale-paid-accounts-table mb-0">
                      <thead><tr><th>Akun kasir / seller</th><th className="text-end">Transaksi lunas</th>{METHODS.filter((method) => method.key !== "other" || amounts.other > 0).map((method) => <th key={method.key} className="text-end">{method.label}</th>)}<th className="text-end">Total omzet</th></tr></thead>
                      <tbody>{breakdown.accounts.map((account) => {
                        const accountMethods = Object.fromEntries(account.methods.map((method) => [method.payment_method, method]));
                        return <tr key={account.cashier_id ?? "unknown"}><th scope="row" className="fw-semibold">{account.cashier_name}</th><td className="text-end">{Number(account.total_transactions).toLocaleString("id-ID")}</td>{METHODS.filter((method) => method.key !== "other" || amounts.other > 0).map((method) => <td className="text-end" key={method.key}><div>{formatIDR(accountMethods[method.key]?.amount || 0)}</div><small className="text-secondary">{Number(accountMethods[method.key]?.total_transactions || 0).toLocaleString("id-ID")} transaksi</small></td>)}<td className="text-end fw-bold">{formatIDR(account.total_amount)}</td></tr>;
                      })}</tbody>
                      <tfoot><tr className="bg-primary-lt fw-bold"><th scope="row">Total</th><td className="text-end">{Number(breakdown.total_transactions || 0).toLocaleString("id-ID")}</td>{METHODS.filter((method) => method.key !== "other" || amounts.other > 0).map((method) => <td className="text-end" key={method.key}>{formatIDR(amounts[method.key] || 0)}</td>)}<td className="text-end">{formatIDR(total)}</td></tr></tfoot>
                    </table>
                  </div>
                )}
                <p className="text-secondary small mt-3 mb-0">Dikelompokkan berdasarkan akun pembuat penjualan, bukan akun yang memproses pelunasan. Transaksi dengan beberapa metode dihitung pada setiap metode yang digunakan, tetapi jumlah transaksi lunas tetap unik.</p>
              </>
            ) : (
              <>
                <section className="sale-paid-revenue-total">
                  <span className="text-secondary small">Total omzet lunas</span>
                  <strong>{formatIDR(total)}</strong>
                  <span className="text-secondary small">{Number(breakdown.total_transactions || 0).toLocaleString("id-ID")} transaksi lunas</span>
                </section>

                <section className="purchase-section sale-paid-revenue-methods" aria-label="Rincian metode pembayaran">
                  <h3 className="fs-4 mb-2">Metode pembayaran</h3>
                  {METHODS.filter((method) => method.key !== "other" || amounts.other > 0).map((method) => (
                    <div className="sale-paid-revenue-method" key={method.key}>
                      <span className="avatar bg-primary-lt text-primary rounded-3" aria-hidden="true">
                        <svg viewBox="0 0 24 24"><path d={method.path} />{method.circle ? <circle cx="12" cy="12" r="2" /> : null}</svg>
                      </span>
                      <span className="sale-paid-revenue-method-label">{method.label}</span>
                      <strong>{formatIDR(amounts[method.key] || 0)}</strong>
                    </div>
                  ))}
                </section>
                {total === 0 ? <p className="small text-secondary mt-3 mb-0">Belum ada omzet dari transaksi lunas pada filter ini.</p> : null}
                <p className="sale-paid-revenue-note text-secondary small mb-0">
                  Mengikuti tanggal transaksi, pencarian, dan status yang dipilih di tabel. Draft, belum lunas, pembatalan, dan uang kembalian tidak dihitung.
                </p>
                {amounts.other > 0 ? <p className="small text-secondary mt-2 mb-0">Metode yang belum tercatat jelas pada data lama ditampilkan terpisah, tanpa mengubah total omzet.</p> : null}
              </>
            )}
          </div>
          <div className="modal-footer purchase-modal-footer">
            {accountView ? <button ref={backButton} type="button" className="btn btn-primary ms-auto" onClick={returnToSummary}>Kembali ke ringkasan</button> : <>
              {canViewAccounts && <button ref={accountsTrigger} type="button" className="btn btn-1 bg-primary-lt" disabled={loading || !hasBreakdown} onClick={(event) => { event.stopPropagation(); setShowAccounts(true); }}><svg className="icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><circle cx="9" cy="7" r="4" /><path d="M3 21v-3a6 6 0 0 1 12 0v3M17 4a4 4 0 0 1 0 8m1 3a5 5 0 0 1 3 5" /></svg>Rincian per akun</button>}
              <button type="button" className="btn btn-primary ms-auto" data-bs-dismiss="modal">Tutup</button>
            </>}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaidRevenueModal;
